/**
 * Joints — what it costs to grow a ship outward.
 *
 * A line has to connect at both ends, and on an existing hull that is usually
 * free: drawing across a corner to smooth it lands on two points that are
 * already there, and costs nothing but the line.
 *
 * Growing is the expensive case. To reach somewhere the ship does not already
 * touch, the new line has an end hanging in space, and holding that end steady
 * takes a joint. The joint stays there for as long as the end is loose, so it
 * is refunded the moment another line arrives to meet it.
 *
 * ── Derived, not tracked ────────────────────────────────────────────────────
 *
 * Which is to say: a joint is in use exactly where a joint has one line on it.
 * Two or more lines hold each other and need nothing; one line needs a joint.
 * Reading that off the graph rather than storing it means the refund cannot be
 * missed and the count cannot drift out of step with the ship — a design that
 * is edited as heavily as this one would find any other arrangement.
 */
/** How many lines meet at each joint. */
export function jointDegrees(graph) {
    const degrees = new Map();
    for (const joint of graph.joints)
        degrees.set(joint.id, 0);
    for (const line of graph.lines) {
        degrees.set(line.a, (degrees.get(line.a) ?? 0) + 1);
        degrees.set(line.b, (degrees.get(line.b) ?? 0) + 1);
    }
    return degrees;
}
/**
 * Ends still hanging in space — one line and nothing to meet it.
 *
 * These are what joints are paying for, and they are also exactly the ends a
 * player is part way through building from.
 */
export function floatingEnds(graph) {
    const out = [];
    for (const [id, degree] of jointDegrees(graph)) {
        if (degree === 1)
            out.push(id);
    }
    return out;
}
/** Joints currently spent on this design. */
export function jointsInUse(graph) {
    return floatingEnds(graph).length;
}
/**
 * What placing a line between two points would cost in joints.
 *
 * Both ends are considered independently, because the interesting cases are
 * asymmetric: a line reaching from a loose end out into space frees one joint
 * and spends another, and should come out even.
 */
export function jointCostOf(graph, a, b) {
    const degrees = jointDegrees(graph);
    const degreeAt = (point) => {
        const joint = graph.joints.find((j) => j.x === point.x && j.y === point.y);
        return joint ? (degrees.get(joint.id) ?? 0) : 0;
    };
    let spent = 0;
    let refunded = 0;
    for (const end of [a, b]) {
        const degree = degreeAt(end);
        // Nothing there yet: this end will hang in space, and needs a joint.
        if (degree === 0)
            spent++;
        // Exactly one line there: that end was loose and is about to be held by
        // two lines, so its joint comes back.
        else if (degree === 1)
            refunded++;
        // Two or more: already a proper junction, and free to build from.
    }
    return { spent, refunded, net: spent - refunded };
}
//# sourceMappingURL=joints.js.map